import React from 'react';
import {
    Typography,
    Box,
    TextField,
    Select,
    MenuItem,
    Button,
    Grid,
    FormControl,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Chip,
    SelectChangeEvent,
    InputLabel,
} from '@mui/material';
import {
    Warning as WarningIcon,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { Colors } from '../../../styles/colors';

import SimpleDatePicker from '../../../components/SimpleDatePicker';
import { RequestType } from '../../../data/verification/verification.models';
import {
    FormValues,
    requestTypes,
    initialValues,
    validationSchema,
    formatStateInput,
    formatSsnInput,
    formatEinInput,
    formatPhoneInput,
    getFieldWarning
} from './logic';
import {
    buttonRowStyles,
    cancelButtonStyles,
    submitButtonStyles,
    disabledSelectStyles,
    errorTextStyles,
    fieldWrapperStyles,
    requestTypeChipsStyles
} from './styles';

interface ManualVerificationFormProps {
    onSubmit: (values: FormValues) => void;
    onCancel: () => void;
    isLoading: boolean;
}

const ManualVerificationForm: React.FC<ManualVerificationFormProps> = ({
    onSubmit,
    onCancel,
    isLoading
}) => {

    const renderFieldWarning = (fieldName: string, value: string) => {
        const warning = getFieldWarning(fieldName, value);
        return warning ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5, ml: 1 }}>
                <WarningIcon sx={{ fontSize: 16, color: Colors.WARNING_ORANGE, mr: 0.5, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: Colors.WARNING_ORANGE, lineHeight: 1.2, wordWrap: 'break-word', flex: 1 }}>
                    {warning}
                </Typography>
            </Box>
        ) : null;
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ values, errors, touched, setFieldValue, isValid }) => {
                const handleRequestTypeChange = (event: SelectChangeEvent<typeof values.request_type>) => {
                    const value = event.target.value;
                    setFieldValue('request_type', value);
                };

                console.log("values", values.business_street_line_1);


                return (
                    <Form style={{ height: '100%', display: 'flex', flexDirection: 'column', }}>
                        <Box sx={{ flex: 1, py: 2, px: 4 }}>
                            {/* Request Type Section */}
                            <Box sx={{ mb: 4 }}>
                                <Field name="request_type">
                                    {({ field }) => (
                                        <Box sx={fieldWrapperStyles}>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: Colors.PRIMARY_DARK, mb: 2 }}>Request type</Typography>
                                            <FormControl fullWidth>
                                                <InputLabel sx={{ fontSize: '14px', color: Colors.NEUTRAL_GRAY }}>Request type</InputLabel>
                                                <Select
                                                    {...field}
                                                    multiple
                                                    value={values.request_type}
                                                    label="Request type"
                                                    onChange={handleRequestTypeChange}
                                                    input={<OutlinedInput label="Request type" />}
                                                    displayEmpty
                                                    size='small'
                                                    error={touched.request_type && !!errors.request_type}
                                                    renderValue={(selected: RequestType[]) => {
                                                        if (selected.length === 0) {
                                                            return <Typography sx={{ color: '#9CA3AF' }}>Select request type</Typography>;
                                                        }
                                                        return (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {selected.map((value: RequestType) => (
                                                                    <Chip
                                                                        key={value}
                                                                        label={value}
                                                                        size="small"
                                                                        sx={requestTypeChipsStyles}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        );
                                                    }}
                                                >
                                                    {requestTypes.map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            <Checkbox
                                                                checked={values.request_type.indexOf(type) > -1}
                                                                sx={{
                                                                    color: '#C4C9DA',
                                                                    '&.Mui-checked': {
                                                                        color: Colors.BRAND_BLUE,
                                                                    },
                                                                }}
                                                            />
                                                            <ListItemText primary={type} sx={{ color: Colors.NEUTRAL_GRAY }} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Box
                                                className={`error-container ${touched.request_type && errors.request_type ? 'has-error' : ''}`}
                                            >
                                                {touched.request_type && errors.request_type && (
                                                    <Typography variant="caption" sx={errorTextStyles}>
                                                        {errors.request_type}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Field>
                            </Box>

                            {/* Two Column Layout */}
                            <Grid container spacing={12}>
                                {/* Business Column */}
                                <Grid item xs={12} md={6}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 600, color: Colors.PRIMARY_DARK, mb: 2 }}>
                                                                            Business
                                                                        </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Field name="business_legal_name">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Legal name"
                                                        size='small'
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        fullWidth
                                                        error={touched.business_legal_name && !!errors.business_legal_name}
                                                    />
                                                    <Box
                                                        className={`error-container ${touched.business_legal_name && errors.business_legal_name ? 'has-error' : ''}`}
                                                    >
                                                        {touched.business_legal_name && errors.business_legal_name && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.business_legal_name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="business_street_line_1">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Street line 1"
                                                        size='small'
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        fullWidth
                                                        error={touched.business_street_line_1 && !!errors.business_street_line_1}
                                                    />
                                                    <Box
                                                        className={`error-container ${touched.business_street_line_1 && errors.business_street_line_1 ? 'has-error' : ''}`}
                                                    >
                                                        {touched.business_street_line_1 && errors.business_street_line_1 && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.business_street_line_1}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="business_street_line_2">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Street line 2"
                                                        size='small'
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        fullWidth
                                                        error={touched.business_street_line_2 && !!errors.business_street_line_2}
                                                    />
                                                    <Box
                                                        className={`error-container ${touched.business_street_line_2 && errors.business_street_line_2 ? 'has-error' : ''}`}
                                                    >
                                                        {touched.business_street_line_2 && errors.business_street_line_2 && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.business_street_line_2}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Field name="business_city">
                                                    {({ field }) => (
                                                        <Box sx={fieldWrapperStyles}>
                                                            <TextField
                                                                {...field}
                                                                label="City"
                                                                fullWidth
                                                                size='small'
                                                                sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                                error={touched.business_city && !!errors.business_city}
                                                            />
                                                            <Box
                                                                className={`error-container ${touched.business_city && errors.business_city ? 'has-error' : ''}`}
                                                            >
                                                                {touched.business_city && errors.business_city && (
                                                                    <Typography variant="caption" sx={errorTextStyles}>
                                                                        {errors.business_city}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Field name="business_state">
                                                    {({ field }) => (
                                                        <Box sx={fieldWrapperStyles}>
                                                            <TextField
                                                                {...field}
                                                                label="State"
                                                                sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                                fullWidth
                                                                size='small'
                                                                error={touched.business_state && !!errors.business_state}
                                                                placeholder="Enter state abbreviation (e.g., CA, NY)"
                                                            />
                                                            <Box
                                                                className={`error-container ${(touched.business_state && errors.business_state) || getFieldWarning('business_state', values.business_state) ? 'has-error' : ''}`}
                                                            >
                                                                {touched.business_state && errors.business_state && (
                                                                    <Typography variant="caption" sx={errorTextStyles}>
                                                                        {errors.business_state}
                                                                    </Typography>
                                                                )}
                                                                {!errors.business_state && renderFieldWarning('business_state', values.business_state)}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Field name="business_zip_code">
                                                    {({ field }) => (
                                                        <Box sx={fieldWrapperStyles}>
                                                            <TextField
                                                                {...field}
                                                                placeholder="Zipcode"
                                                                label="Zipcode"
                                                                sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                                fullWidth
                                                                size='small'
                                                                error={touched.business_zip_code && !!errors.business_zip_code}
                                                            />
                                                            <Box
                                                                className={`error-container ${(touched.business_zip_code && errors.business_zip_code) || getFieldWarning('business_zip_code', values.business_zip_code) ? 'has-error' : ''}`}
                                                            >
                                                                {touched.business_zip_code && errors.business_zip_code && (
                                                                    <Typography variant="caption" sx={errorTextStyles}>
                                                                        {errors.business_zip_code}
                                                                    </Typography>
                                                                )}
                                                                {!errors.business_zip_code && renderFieldWarning('business_zip_code', values.business_zip_code)}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Field name="business_country">
                                                    {({ field }) => (
                                                        <FormControl fullWidth>
                                                            <Select
                                                                {...field}
                                                                value="US"
                                                                disabled
                                                                sx={{ ...disabledSelectStyles, '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                                size='small'
                                                            >
                                                                <MenuItem value="US">US</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>

                                        <Field name="business_website">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Website"
                                                        fullWidth
                                                        size='small'
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                    />
                                                    <Box
                                                        className={`error-container ${getFieldWarning('business_website', values.business_website) ? 'has-error' : ''}`}
                                                    >
                                                        {renderFieldWarning('business_website', values.business_website)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="business_ein">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="EIN"
                                                        fullWidth
                                                        size='small'
                                                        placeholder="XX-XXXXXXX"
                                                        onChange={(e) => {
                                                            const formatted = formatEinInput(e.target.value);
                                                            field.onChange({ target: { name: field.name, value: formatted } });
                                                        }}
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                    />
                                                    <Box
                                                        className={`error-container ${getFieldWarning('business_ein', values.business_ein) ? 'has-error' : ''}`}
                                                    >
                                                        {renderFieldWarning('business_ein', values.business_ein)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>
                                    </Box>
                                </Grid>

                                {/* Applicant Column */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0E1944', mb: 2 }}>
                                        Applicant
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Field name="applicant_first_name">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="First name"
                                                        fullWidth
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        error={touched.applicant_first_name && !!errors.applicant_first_name}
                                                        size='small'
                                                    />
                                                    <Box
                                                        className={`error-container ${touched.applicant_first_name && errors.applicant_first_name ? 'has-error' : ''}`}
                                                    >
                                                        {touched.applicant_first_name && errors.applicant_first_name && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.applicant_first_name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="applicant_last_name">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Last name"
                                                        fullWidth
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        error={touched.applicant_last_name && !!errors.applicant_last_name}
                                                        size='small'
                                                    />
                                                    <Box
                                                        className={`error-container ${touched.applicant_last_name && errors.applicant_last_name ? 'has-error' : ''}`}
                                                    >
                                                        {touched.applicant_last_name && errors.applicant_last_name && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.applicant_last_name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="applicant_email">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Email"
                                                        fullWidth
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        error={touched.applicant_email && !!errors.applicant_email}
                                                        size='small'
                                                    />
                                                    <Box
                                                        className={`error-container ${(touched.applicant_email && errors.applicant_email) || getFieldWarning('applicant_email', values.applicant_email) ? 'has-error' : ''}`}
                                                    >
                                                        {touched.applicant_email && errors.applicant_email && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.applicant_email}
                                                            </Typography>
                                                        )}
                                                        {!errors.applicant_email && renderFieldWarning('applicant_email', values.applicant_email)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="applicant_phone">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Phone number"
                                                        fullWidth
                                                        placeholder="(XXX) XXX-XXXX"
                                                        onChange={(e) => {
                                                            const formatted = formatPhoneInput(e.target.value);
                                                            field.onChange({ target: { name: field.name, value: formatted } });
                                                        }}
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        error={touched.applicant_phone && !!errors.applicant_phone}
                                                        size='small'
                                                    />
                                                    <Box
                                                        className={`error-container ${(touched.applicant_phone && errors.applicant_phone) || getFieldWarning('applicant_phone', values.applicant_phone) ? 'has-error' : ''}`}
                                                    >
                                                        {touched.applicant_phone && errors.applicant_phone && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.applicant_phone}
                                                            </Typography>
                                                        )}
                                                        {!errors.applicant_phone && renderFieldWarning('applicant_phone', values.applicant_phone)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="applicant_date_of_birth">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <SimpleDatePicker
                                                        value={field.value}
                                                        onChange={(value) => setFieldValue('applicant_date_of_birth', value)}
                                                        placeholder="Date of birth"
                                                    />
                                                </Box>
                                            )}
                                        </Field>

                                        <Field name="applicant_last_4_ssn">
                                            {({ field }) => (
                                                <Box sx={fieldWrapperStyles}>
                                                    <TextField
                                                        {...field}
                                                        label="Last 4 SSN"
                                                        fullWidth
                                                        sx={{ '& .MuiInputLabel-root': { color: '#9CA3AF' } }}
                                                        error={touched.applicant_last_4_ssn && !!errors.applicant_last_4_ssn}
                                                        inputProps={{
                                                            maxLength: 4,
                                                            pattern: '[0-9]*',
                                                            inputMode: 'numeric'
                                                        }}
                                                        onChange={(e) => {
                                                            const value = formatSsnInput(e.target.value);
                                                            setFieldValue('applicant_last_4_ssn', value);
                                                        }}
                                                        size='small'
                                                    />
                                                    <Box
                                                        className={`error-container ${(touched.applicant_last_4_ssn && errors.applicant_last_4_ssn) || getFieldWarning('applicant_last_4_ssn', values.applicant_last_4_ssn) ? 'has-error' : ''}`}
                                                    >
                                                        {touched.applicant_last_4_ssn && errors.applicant_last_4_ssn && (
                                                            <Typography variant="caption" sx={errorTextStyles}>
                                                                {errors.applicant_last_4_ssn}
                                                            </Typography>
                                                        )}
                                                        {!errors.applicant_last_4_ssn && renderFieldWarning('applicant_last_4_ssn', values.applicant_last_4_ssn)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Field>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Buttons */}
                        <Box sx={buttonRowStyles}>
                            <Button
                                onClick={onCancel}
                                disabled={isLoading}
                                sx={cancelButtonStyles}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                                sx={submitButtonStyles}
                            >
                                Create
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ManualVerificationForm;
